import React from 'react';
import FloatingMenu from '../FloatingMenu';
import { Box, Paper, Typography, Grid, ListItemIcon, Container } from '@mui/material';
import { motion } from 'framer-motion';

// Import relevant icons (adjust paths/names as needed)
import IconRecipe from '../../assets/images/icon-intro.svg'; // Using intro icon for suggestions
import IconCustomize from '../../assets/images/icon-diet.svg'; // Using diet icon for customization
import IconNutrition from '../../assets/images/icon-tools.svg'; // Using tools icon for nutrition/guidance
import IconInput from '../../assets/images/icon-time.svg'; // Using time icon as placeholder for input methods
import IconIntegrate from '../../assets/images/icon-features.svg'; // Using features icon for integration

// Define styles for icons
const iconStyle = {
    filter: 'invert(20%) sepia(51%) saturate(5000%) hue-rotate(280deg) brightness(95%) contrast(90%)',
    width: '24px',
    height: '24px',
    marginRight: '16px',
};

// Define styles for the image container
const imageContainerStyle = {
    height: '400px', // Increased height for larger images
    width: '100%',
    backgroundColor: '#e0e0e0', // Placeholder background if image fails
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden', // Keep image contained
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Cover the container, might crop
};

const solutionPoints = [
  {
    icon: IconRecipe,
    title: 'Smart Recipe Suggestions',
    description: 'Leverages AI to suggest recipes based on ingredients you already have, minimizing waste and maximizing pantry potential.',
    alt: 'Recipe suggestion icon',
    imageName: 'solution_recipe.png', // Placeholder image name
  },
  {
    icon: IconCustomize,
    title: 'Personalized Customization',
    description: 'Easily adapts recipes to fit dietary restrictions (allergies, health goals) and personal taste preferences. Adjust portion sizes or swap ingredients effortlessly.', // Added more text here
    alt: 'Customization icon',
    imageName: 'solution_customize.png', // Placeholder image name
  },
  {
    icon: IconNutrition,
    title: 'Nutrition & Meal Guidance',
    description: 'Provides detailed nutrition information for recipes and offers intelligent meal suggestions tailored to your needs.',
    alt: 'Nutrition icon',
    imageName: 'solution_nutrition.png', // Placeholder image name
  },
  {
    icon: IconInput,
    title: 'Hands-Free Cooking',
    description: 'Supports both text and voice commands, allowing for a seamless, hands-free cooking experience when you need it most.',
    alt: 'Input methods icon',
    imageName: 'solution_input.png', // Placeholder image name
  },
  {
    icon: IconIntegrate,
    title: 'Integrated Kitchen Hub',
    description: 'Combines recipe discovery, chat assistance, visual aids, and kitchen management tools into one intelligent platform.',
    alt: 'Integration icon',
    imageName: 'solution_integrate.png', // Placeholder image name
  },
];

// Animation variants for the container to stagger children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // Stagger the animation of children by 0.2 seconds
    },
  },
};

// Animation variants for each card item
const itemVariants = {
  hidden: { y: -50, opacity: 0 }, // Start 50px above and invisible
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring', // Optional: Use a spring animation
      stiffness: 100,
    },
  },
};


const Solution = () => (
  <Box className="page-section" sx={{ py: { xs: 4, md: 6 }, background: '#f0f0f0' }}>
    <FloatingMenu />
    <Container maxWidth="lg">
      {/* Title Box */}
      <Paper
        elevation={2}
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: '12px',
          background: '#c3eafc', // Different background color for Solution
          mb: 6,
          textAlign: 'center',
        }}
      >
        <Typography variant="h3" component="h1" sx={{ color: '#034078', fontWeight: 'bold' }}>
          Our Intelligent Solution ✨
        </Typography>
      </Paper>

      {/* Grid container wrapped with motion.div */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Grid container spacing={4} justifyContent="center">
          {solutionPoints.map((point, index) => (
            <Grid item xs={12} sm={6} md={5} key={index} component={motion.div} variants={itemVariants} style={{ height: '100%' }}>
              <Paper
                elevation={3}
                sx={{
                  padding: 3,
                  borderRadius: '12px',
                  background: '#E0F7FA', // Lighter card background
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Title and Icon Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 'auto' }}>
                    <img src={point.icon} alt={point.alt} style={iconStyle} />
                  </ListItemIcon>
                  <Typography variant="h5" component="h3" sx={{ color: '#034078', fontWeight: 500 }}>
                    {point.title}
                  </Typography>
                </Box>

                {/* Image Container */}
                <Box sx={imageContainerStyle}>
                  <img
                    // Dynamically require the image based on imageName
                    // Ensure you have a 'solution' subfolder in assets/images
                    src={require(`../../assets/images/solution/${point.imageName}`)}
                    alt={`${point.title} illustration`}
                    style={imageStyle}
                    // Optional: Add error handling for missing images
                    onError={(e) => { e.target.style.display = 'none'; /* Hide img tag */ e.target.parentElement.innerHTML = '<span>Image not found</span>'; /* Show text in container */ }}
                  />
                </Box>

                {/* Description Text */}
                <Typography variant="body1" sx={{ color: '#444' }}>
                  {point.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Container>
  </Box>
);

export default Solution;
